import { useEffect, useRef, useState } from "react";
import type { LabelPlanSourceDetail } from "../../../shared/labels/work-order-request-contracts";
import { uiFault, type LabelsApi } from "./labels-api";

export function usePlanSourceLinkWorkspace(api: LabelsApi, sourceId: string | null, rowId: string | null) {
  const [detail, setDetail] = useState<LabelPlanSourceDetail | null>(null);
  const [fault, setFault] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [success, setSuccess] = useState<string | null>(null);
  const busyRef = useRef(false);
  const mutation = useRef<AbortController | null>(null);
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setDetail(null); setFault(null); setBusy(false); setLoading(Boolean(sourceId && rowId));
    if (sourceId && rowId) void api.planSource(sourceId, controller.signal).then(value => { if (!controller.signal.aborted) setDetail(value); })
      .catch((error: unknown) => { if (!controller.signal.aborted) setFault(uiFault(error).message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { controller.abort(); mutation.current?.abort(); busyRef.current = false; };
  }, [api, sourceId, rowId, reload]);
  const row = detail?.rows.find(item => item.row.id === rowId) ?? null;
  async function link(candidateToken: string, requestId: string, variantKey: string, reason: string, confirmed: boolean) {
    if (!sourceId || !rowId || !confirmed || busyRef.current) return;
    const input = { requestId, variantKey, matchToken: candidateToken, confirmed: true as const, reason: reason.trim() };
    const fingerprint = JSON.stringify(input);
    if (attempt.current?.fingerprint !== fingerprint) attempt.current = { fingerprint, key: crypto.randomUUID() };
    const key = attempt.current.key;
    const controller = new AbortController(); mutation.current = controller; busyRef.current = true;
    setBusy(true); setFault(null); setSuccess(null);
    try {
      await api.linkPlanSource(sourceId, rowId, input, key, controller.signal);
      if (!controller.signal.aborted) { setSuccess("Plan satırı mevcut talebe bağlandı. Özgün kaynak ve talep geçmişi korundu."); setReload(value => value + 1); }
    } catch (error: unknown) { if (!controller.signal.aborted) setFault(uiFault(error).message); }
    finally { if (!controller.signal.aborted) { busyRef.current = false; setBusy(false); } }
  }
  return { row, loading, busy, fault, success, link, retry: () => setReload(value => value + 1) };
}
